import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useImageFallback } from '@/hooks/useImageFallback.ts';
import GhostShape from '@/components/common/svg/shapes/GhostShape.tsx';
import { thumbHashToImageUrl } from '@/utils/thumbhash.ts';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string;
  thumbhash?: string;
  customLoader?: JSX.Element;
  fullWidth?: boolean;
  fallbackBackgroundColor?: string;
  fallbackIconWidth?: number;
  fallbackIconHeight?: number;
  forceLoading?: boolean;
}

export const ImageWithLoader = ({
  fallback,
  thumbhash,
  src,
  alt,
  onError,
  onLoad,
  style,
  customLoader,
  fullWidth = true,
  fallbackBackgroundColor = 'linear-gradient(0deg, #E57575 0%, #FF9393 100%)',
  fallbackIconWidth = 30,
  fallbackIconHeight = 40,
  forceLoading = false,
  ...props
}: ImageProps) => {
  const { imageSrc, handleError } = useImageFallback(src, fallback);
  const [loaded, setLoaded] = useState(false);
  const [fallbackLoaded, setFallbackLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const thumbhashDataUrl = useMemo(() => {
    if (!thumbhash) return undefined;
    try {
      return thumbHashToImageUrl(thumbhash);
    } catch {
      return undefined;
    }
  }, [thumbhash]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
    setFallbackLoaded(false);
  }, [src, fallback]);
  const isImageLoading = !loaded && !!imageSrc;
  const showLoader = isImageLoading || forceLoading;
  const showThumbhash = showLoader && !!thumbhashDataUrl;
  const showFallbackAsPlaceholder =
    showLoader && !thumbhashDataUrl && !!fallback && fallbackLoaded;

  const hasThumbhash = !!thumbhashDataUrl;

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        height: '100%',
        width: fullWidth ? '100%' : undefined,
        overflow: 'hidden',
        ...(hasThumbhash ? style : undefined),
        objectFit: undefined,
        opacity: undefined
      }}
    >
      {thumbhashDataUrl && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${thumbhashDataUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(4px)',
            opacity: loaded ? 0 : 1,
            transition: 'opacity 0.2s ease-out',
            zIndex: 1,
            pointerEvents: 'none',
            borderRadius: style?.borderRadius
          }}
        />
      )}

      {!thumbhashDataUrl && fallback && isImageLoading && (
        <img
          {...props}
          src={fallback}
          alt={alt}
          onLoad={() => setFallbackLoaded(true)}
          style={{
            display: showFallbackAsPlaceholder ? 'block' : 'none',
            ...style
          }}
        />
      )}

      {showLoader && (
        <div
          style={{
            position:
              showThumbhash || showFallbackAsPlaceholder || forceLoading
                ? 'absolute'
                : undefined,
            inset:
              showThumbhash || showFallbackAsPlaceholder || forceLoading
                ? 0
                : undefined,
            zIndex:
              showThumbhash || showFallbackAsPlaceholder || forceLoading
                ? 4
                : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            ...(showThumbhash || showFallbackAsPlaceholder || forceLoading
              ? { height: '100%' }
              : style)
          }}
        >
          {customLoader ? (
            customLoader
          ) : (
            <div
              style={{
                width: 24,
                height: 24,
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite'
              }}
            />
          )}
        </div>
      )}

      {imageSrc ? (
        <img
          {...props}
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          decoding="async"
          onLoad={(e) => {
            setLoaded(true);
            onLoad?.(e);
          }}
          onError={(e) => {
            handleError(e);
            onError?.(e);
          }}
          style={{
            ...(hasThumbhash
              ? {
                  width: '100%',
                  height: '100%',
                  objectFit: style?.objectFit ?? ('cover' as const),
                  borderRadius: style?.borderRadius,
                  opacity: loaded ? 1 : 0,
                  transition: 'opacity 0.2s ease-out'
                }
              : {
                  display: isImageLoading ? 'none' : 'block',
                  ...style
                })
          }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            background: fallbackBackgroundColor,
            ...style
          }}
        >
          <GhostShape
            width={fallbackIconWidth}
            height={fallbackIconHeight}
            fillColor="#ffffff"
          />
        </div>
      )}
    </div>
  );
};
